import { Steps } from "antd";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import {
  clearCreateEquipmentState,
  createEquipment as createEquipmentApi,
  fetchCompanyBranchesDropdown,
  fetchEquipmentManufacturersDropdown,
  fetchEquipmentModelsDropdown,
  fetchEquipmentTypesDropdown,
  fetchEquipmentVendorsDropdown,
} from "../../../store/Slices/EquipmentSlice";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/Issue.css";
import "../../../styles/Trip.css";
import {
  findCollectionByKeys,
  normalizeOptions,
  findOptionByValueOrLabel,
  modules,
  formats,
} from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";
import {
  brakeSystemOptions,
  containerSizeOptions,
  installmentFrequencyOptions,
  suspensionTypeOptions,
  getNextEquipmentSystemId,
} from "./equipmentAssets.constants";

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const isPresent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const getValueByPaths = (source, paths = []) => {
  if (!source || !paths.length) return undefined;

  for (const path of paths) {
    const segments = String(path)
      .split(".")
      .map((segment) => segment.trim())
      .filter(Boolean);

    let current = source;
    let validPath = true;

    for (const segment of segments) {
      if (current && typeof current === "object" && segment in current) {
        current = current[segment];
      } else {
        validPath = false;
        break;
      }
    }

    if (validPath && isPresent(current)) {
      return current;
    }
  }

  return undefined;
};

const normalizeLookupValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "")
    .replace(/[^a-z0-9]/g, "");

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCapacity = (capacityValue, capacityUnit) => {
  const numericCapacity = toNumber(capacityValue);
  return `${numericCapacity.toLocaleString("en-US")} ${capacityUnit || ""}`.trim();
};

const formatUsage = (usageValue, usageType) => {
  const numericUsage = toNumber(usageValue);
  const formattedUsage = numericUsage.toLocaleString("en-US");

  if (usageType === "hours") return `${formattedUsage} hrs`;
  if (usageType === "trips") return `${formattedUsage} trips`;
  if (usageType === "volume") return `${formattedUsage} volume`;
  return formattedUsage;
};

const normalizeCapacityUnit = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "l" || normalized === "liter" || normalized === "liters") {
    return "litres";
  }
  if (normalized === "ton" || normalized === "tons") return "tons";
  if (normalized === "unit" || normalized === "units") return "units";
  return normalized;
};

const normalizeUsageMeterType = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "trip" || normalized === "trips") return "trips";
  if (normalized === "hour" || normalized === "hours") return "hours";
  if (normalized === "volume") return "volume";
  return normalized;
};

const isInstallmentPayment = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "installments" || normalized === "installment";
};

const normalizePaymentValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const paymentMethodValues = ["bank_transfer", "cash", "lease", "financing"];

const normalizePaymentType = (value, paymentMethod = "") => {
  const normalizedValue = normalizePaymentValue(value);
  const normalizedMethod = normalizePaymentValue(paymentMethod);

  if (normalizedValue === "full_payment" || normalizedValue === "installments") {
    return normalizedValue;
  }

  if (
    normalizedValue === "installment" ||
    normalizedMethod === "installments" ||
    normalizedMethod === "installment"
  ) {
    return "installments";
  }

  if (
    paymentMethodValues.includes(normalizedValue) ||
    paymentMethodValues.includes(normalizedMethod)
  ) {
    return "full_payment";
  }

  return "";
};

const normalizePaymentMethod = (value, paymentType = "") => {
  const normalizedValue = normalizePaymentValue(value);
  const normalizedType = normalizePaymentValue(paymentType);

  if (paymentMethodValues.includes(normalizedValue)) {
    return normalizedValue;
  }

  if (paymentMethodValues.includes(normalizedType)) {
    return normalizedType;
  }

  return "";
};

const toApiCapacityUnit = (value) => {
  const normalized = normalizeCapacityUnit(value);
  if (normalized === "litres") return "liters";
  return normalized;
};

const toApiPaymentType = (value) => {
  if (isInstallmentPayment(value)) return "installment";
  return String(value || "").trim().toLowerCase();
};

const getOptionLabel = (options = [], value = "", fallback = "N/A") =>
  options.find((option) => {
    const optionValue = String(option?.value ?? "");
    const optionLabel = String(option?.label ?? "");
    const targetValue = String(value ?? "");

    return (
      optionValue === targetValue ||
      optionLabel.toLowerCase() === targetValue.toLowerCase() ||
      normalizeLookupValue(optionValue) === normalizeLookupValue(targetValue) ||
      normalizeLookupValue(optionLabel) === normalizeLookupValue(targetValue)
    );
  })?.label || fallback;

const getInitialEquipmentValues = (editingEquipment) => {
  const systemId = editingEquipment?.systemId || getNextEquipmentSystemId();
  const extra = editingEquipment?.formData || {};

  return {
    systemId,
    registrationNumber:
      editingEquipment?.registrationNumber || extra.registrationNumber || "",
    equipmentType:
      extra.equipmentTypeId || editingEquipment?.equipmentTypeId || extra.equipmentType || "",
    serialNumber: editingEquipment?.serialNumber || extra.serialNumber || "",
    capacityValue: editingEquipment?.capacityValue || extra.capacityValue || "",
    capacityUnit: normalizeCapacityUnit(editingEquipment?.capacityUnit || extra.capacityUnit),
    usageMeterType: normalizeUsageMeterType(
      editingEquipment?.usageMeterType || extra.usageMeterType
    ),
    usageMeterReadingValue:
      editingEquipment?.usageMeterReadingValue ||
      extra.usageMeterReadingValue ||
      "",
    branch: extra.branchId || editingEquipment?.branchId || extra.branch || "",
    status: editingEquipment?.status || extra.status || "active",
    description: extra.description || "",
    manufacturer: extra.manufacturerId || extra.manufacturer || "",
    model: extra.modelId || extra.model || "",
    yearOfManufacture: extra.yearOfManufacture || "",
    supplierVendor:
      extra.supplierVendorId || extra.supplierVendor || extra.vendorId || extra.vendor || "",
    assetNumber: extra.assetNumber || "",
    numberOfAxles: extra.numberOfAxles || "",
    brakeSystem: extra.brakeSystem || "",
    numberOfTyres: extra.numberOfTyres || editingEquipment?.linkedTyres || "",
    suspensionType: extra.suspensionType || "",
    tankerBowserCapacity:
      extra.tankerBowserCapacity || editingEquipment?.capacityValue || extra.capacityValue || "",
    numberOfCompartments: extra.numberOfCompartments || "",
    loadCapacity: extra.loadCapacity || "",
    containerSize: extra.containerSize || "",
    length: extra.length || "",
    height: extra.height || "",
    width: extra.width || "",
    tareWeight: extra.tareWeight || "",
    grossWeight: extra.grossWeight || "",

    purchaseDate: extra.purchaseDate || "",
    purchaseCost: extra.purchaseCost || editingEquipment?.totalCost || "",
    paymentType: normalizePaymentType(extra.paymentType, extra.paymentMethod),
    paymentMethod: normalizePaymentMethod(extra.paymentMethod, extra.paymentType),
    vendor: extra.vendorId || extra.vendor || "",
    depreciationMethod: extra.depreciationMethod || "",
    usefulLife: extra.usefulLife || "",
    residualValue: extra.residualValue || "",

    totalInstallments: extra.totalInstallments || "",
    installmentAmount: extra.installmentAmount || "",
    installmentFrequency: extra.installmentFrequency || "",
    firstPaymentDate: extra.firstPaymentDate || "",
    interestRate: extra.interestRate || "",
    financingInstitution: extra.financingInstitution || "",

    insuranceProvider: extra.insuranceProvider || "",
    policyNumber: extra.policyNumber || "",
    coverageType: extra.coverageType || "",
    coverageAmount: extra.coverageAmount || "",
    premiumAmount: extra.premiumAmount || "",
    insuranceStartDate: extra.insuranceStartDate || "",
    insuranceExpiryDate: extra.insuranceExpiryDate || editingEquipment?.nextServiceDue || "",

    attachments: extra.attachments || [],
    notes: extra.notes || editingEquipment?.notes || "",
  };
};

const mapFiles = (files = []) =>
  (files || [])
    .map((file) => file?.fileName || file?.file_path || file?.path || file)
    .filter(Boolean);

const buildCreateEquipmentPayload = (values) => {
  const payload = {
    registration_number: String(values.registrationNumber || "").trim(),
    equipment_type_id: Number(values.equipmentType),
    serial_number: String(values.serialNumber || "").trim(),
    capacity: Number(values.capacityValue),
    capacity_unit: toApiCapacityUnit(values.capacityUnit),
    usage_meter_type: String(values.usageMeterType || "").trim(),
    branch_id: Number(values.branch),
    status: String(values.status || "").trim(),
    description: values.description || "",
    manufacturer_id: Number(values.manufacturer),
    model_id: Number(values.model),
    year_of_manufacture: Number(values.yearOfManufacture),
    supplier_vendor_id: Number(values.supplierVendor),
    asset_number: String(values.assetNumber || "").trim(),
    number_of_axles: Number(values.numberOfAxles),
    number_of_tyres: Number(values.numberOfTyres),
    brake_system: String(values.brakeSystem || "").trim(),
    suspension_type: String(values.suspensionType || "").trim(),
    tanker_bowser_capacity: Number(values.tankerBowserCapacity),
    number_of_compartments: Number(values.number_of_compartments),
    load_capacity: Number(values.load_capacity),
    container_size: String(values.containerSize || "").trim(),
    length: Number(values.length),
    width: Number(values.width),
    height: Number(values.height),
    tare_weight: Number(values.tareWeight),
    gross_weight: Number(values.grossWeight),
    notes: stripHtml(values.notes),
    asset_purchases: {
      purchase_at: values.purchaseDate,
      purchase_cost: Number(values.purchaseCost),
      payment_type: toApiPaymentType(values.paymentType),
      payment_method: String(values.paymentMethod || "").trim(),
      vendor_id: Number(values.vendor),
      depreciation_method: String(values.depreciationMethod || "").trim(),
      useful_life: Number(values.usefulLife),
      residual_value: Number(values.residualValue),
    },
    asset_insurances: {
      provider: String(values.insuranceProvider || "").trim(),
      policy_number: String(values.policyNumber || "").trim(),
      coverage_type: String(values.coverageType || "").trim(),
      coverage_amount: Number(values.coverageAmount),
      premium_amount: Number(values.premiumAmount),
      start_date: values.insuranceStartDate,
      expiry_date: values.insuranceExpiryDate,
    },
    files: mapFiles(values.attachments),
  };

  if (isInstallmentPayment(values.paymentType) || values.totalInstallments || values.installmentAmount) {
    payload.asset_installments = {
      total_installments: Number(values.totalInstallments),
      installment_amount: Number(values.installmentAmount),
      installment_frequency: String(values.installmentFrequency || "").trim(),
      first_payment_date: values.firstPaymentDate,
      interest_rate: Number(values.interestRate),
      financing_institution: String(values.financingInstitution || "").trim(),
    };
  }

  return payload;
};

const buildLocalEquipmentRecord = ({
  values,
  createdEquipmentData,
  editingEquipment,
  systemId,
  equipmentTypeOptions,
  branchOptions,
  manufacturerOptions,
  modelOptions,
  vendorOptions,
}) => {
  const files = mapFiles(values.attachments);
  const equipmentTypeLabel = getOptionLabel(
    equipmentTypeOptions,
    values.equipmentType,
    editingEquipment?.equipmentType || "--"
  );
  const branchLabel = getOptionLabel(
    branchOptions,
    values.branch,
    editingEquipment?.branch || "--"
  );
  const manufacturerLabel = getOptionLabel(
    manufacturerOptions,
    values.manufacturer,
    editingEquipment?.formData?.manufacturer || "N/A"
  );
  const modelLabel = getOptionLabel(
    modelOptions,
    values.model,
    editingEquipment?.formData?.model || "N/A"
  );
  const supplierVendorLabel = getOptionLabel(
    vendorOptions,
    values.supplierVendor,
    editingEquipment?.formData?.supplierVendor || "N/A"
  );
  const purchaseVendorLabel = getOptionLabel(
    vendorOptions,
    values.vendor,
    editingEquipment?.formData?.vendor || "N/A"
  );
  const capacityValue = toNumber(values.capacityValue);
  const usageMeterReadingValue = toNumber(values.usageMeterReadingValue);

  return {
    id:
      getValueByPaths(createdEquipmentData, ["id", "equipment_id"]) ||
      editingEquipment?.id ||
      `custom-${systemId}`,
    isCustom: true,
    systemId,
    registrationNumber: values.registrationNumber,
    equipmentType: equipmentTypeLabel,
    serialNumber: values.serialNumber,
    capacityValue,
    capacityUnit: values.capacityUnit,
    capacity: formatCapacity(capacityValue, values.capacityUnit),
    branch: branchLabel,
    linkedVehicleId:
      editingEquipment?.linkedVehicleId ||
      editingEquipment?.formData?.linkedVehicleId ||
      "N/A",
    linkedTyres: toNumber(editingEquipment?.linkedTyres || values.numberOfTyres),
    usageMeterType: values.usageMeterType,
    usageMeterReadingValue,
    usageMeterReading: formatUsage(usageMeterReadingValue, values.usageMeterType),
    nextServiceDue: values.insuranceExpiryDate || values.purchaseDate || "",
    status: values.status,
    totalCost: toNumber(values.purchaseCost),
    formData: {
      ...values,
      attachments: files,
      linkedVehicleId:
        editingEquipment?.linkedVehicleId ||
        editingEquipment?.formData?.linkedVehicleId ||
        "N/A",
      equipmentTypeId: values.equipmentType,
      equipmentType: equipmentTypeLabel,
      branchId: values.branch,
      branch: branchLabel,
      manufacturerId: values.manufacturer,
      manufacturer: manufacturerLabel,
      modelId: values.model,
      model: modelLabel,
      supplierVendorId: values.supplierVendor,
      supplierVendor: supplierVendorLabel,
      vendorId: values.vendor,
      vendor: purchaseVendorLabel,
    },
    notes: values.notes,
  };
};

const equipmentFormSteps = [
  { title: "General Info" },
  { title: "Purchase Info" },
  { title: "Insurance Info" },
  { title: "Attachment" },
];

const equipmentStepFields = [
  [
    "registrationNumber",
    "equipmentType",
    "serialNumber",
    "capacityValue",
    "capacityUnit",
    "usageMeterType",
    "branch",
    "status",
    "description",
    "manufacturer",
    "model",
    "yearOfManufacture",
    "supplierVendor",
    "assetNumber",
    "numberOfAxles",
    "brakeSystem",
    "numberOfTyres",
    "suspensionType",
    "tankerBowserCapacity",
    "numberOfCompartments",
    "loadCapacity",
    "containerSize",
    "length",
    "height",
    "width",
    "tareWeight",
    "grossWeight",
  ],
  [
    "purchaseDate",
    "purchaseCost",
    "paymentType",
    "paymentMethod",
    "vendor",
    "depreciationMethod",
    "usefulLife",
    "residualValue",
    "totalInstallments",
    "installmentAmount",
    "installmentFrequency",
    "firstPaymentDate",
    "interestRate",
    "financingInstitution",
  ],
  [
    "insuranceProvider",
    "policyNumber",
    "coverageType",
    "coverageAmount",
    "premiumAmount",
    "insuranceStartDate",
    "insuranceExpiryDate",
  ],
  ["attachments"],
];

const equipmentStepSchemas = [
  Yup.object().shape({
    registrationNumber: Yup.string().required("Registration No. is required"),
    equipmentType: Yup.mixed().required("Equipment Type is required"),
    serialNumber: Yup.string().required("Serial Number is required"),
    capacityValue: Yup.number()
      .typeError("Capacity must be numeric")
      .positive("Capacity must be greater than zero")
      .required("Capacity is required"),
    capacityUnit: Yup.string().required("Capacity Unit is required"),
    usageMeterType: Yup.string().required("Usage Meter Type is required"),
    branch: Yup.mixed().required("Branch is required"),
    status: Yup.string().required("Status is required"),
    description: Yup.string()
      .test(
        "description-max",
        "Description too long",
        (value) => stripHtml(value).length <= 1000
      )
      .nullable(),
    manufacturer: Yup.mixed().required("Manufacturer is required"),
    model: Yup.mixed().required("Model is required"),
    yearOfManufacture: Yup.number()
      .typeError("Year of Manufacture must be numeric")
      .min(1900, "Year of Manufacture must be valid")
      .max(2100, "Year of Manufacture must be valid")
      .required("Year of Manufacture is required"),
    supplierVendor: Yup.mixed().required("Supplier / Vendor is required"),
    assetNumber: Yup.string().required("Asset Number is required"),
    numberOfAxles: Yup.number()
      .typeError("Number of Axle must be numeric")
      .integer("Number of Axle must be an integer")
      .positive("Number of Axle must be greater than zero")
      .required("Number of Axle is required"),
    brakeSystem: Yup.string().required("Brake System is required"),
    numberOfTyres: Yup.number()
      .typeError("Number of Tyres must be numeric")
      .integer("Number of Tyres must be an integer")
      .positive("Number of Tyres must be greater than zero")
      .required("Number of Tyres is required"),
    suspensionType: Yup.string().required("Suspension Type is required"),
    tankerBowserCapacity: Yup.number()
      .typeError("Tanker / Bowser Capacity must be numeric")
      .positive("Tanker / Bowser Capacity must be greater than zero")
      .required("Tanker / Bowser Capacity is required"),
    numberOfCompartments: Yup.number()
      .typeError("Number of Compartments must be numeric")
      .integer("Number of Compartments must be an integer")
      .positive("Number of Compartments must be greater than zero")
      .required("Number of Compartments is required"),
    loadCapacity: Yup.number()
      .typeError("Load Capacity must be numeric")
      .positive("Load Capacity must be greater than zero")
      .required("Load Capacity is required"),
    containerSize: Yup.string().required("Container Size is required"),
    length: Yup.number()
      .typeError("Length must be numeric")
      .positive("Length must be greater than zero")
      .required("Length is required"),
    height: Yup.number()
      .typeError("Height must be numeric")
      .positive("Height must be greater than zero")
      .required("Height is required"),
    width: Yup.number()
      .typeError("Width must be numeric")
      .positive("Width must be greater than zero")
      .required("Width is required"),
    tareWeight: Yup.number()
      .typeError("Tare Weight must be numeric")
      .positive("Tare Weight must be greater than zero")
      .required("Tare Weight is required"),
    grossWeight: Yup.number()
      .typeError("Gross Weight must be numeric")
      .positive("Gross Weight must be greater than zero")
      .required("Gross Weight is required"),
  }),
  Yup.object().shape({
    purchaseDate: Yup.date().required("Purchase Date is required"),
    purchaseCost: Yup.number()
      .typeError("Purchase Cost must be numeric")
      .positive("Purchase Cost must be greater than zero")
      .required("Purchase Cost is required"),
    paymentType: Yup.string().required("Payment Type is required"),
    paymentMethod: Yup.string().required("Payment Method is required"),
    vendor: Yup.mixed().required("Vendor is required"),
    depreciationMethod: Yup.string().required("Depreciation Method is required"),
    usefulLife: Yup.number()
      .typeError("Useful Life must be numeric")
      .positive("Useful Life must be greater than zero")
      .required("Useful Life is required"),
    residualValue: Yup.number()
      .typeError("Residual Value must be numeric")
      .min(0, "Residual Value cannot be negative")
      .required("Residual Value is required"),
    totalInstallments: Yup.number()
      .typeError("Total Installments must be numeric")
      .integer("Total Installments must be an integer")
      .positive("Total Installments must be greater than zero")
      .when("paymentType", {
        is: (value) => value === "installments" || value === "installment",
        then: (schema) => schema.required("Total Installments is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    installmentAmount: Yup.number()
      .typeError("Installment Amount must be numeric")
      .positive("Installment Amount must be greater than zero")
      .when("paymentType", {
        is: (value) => value === "installments" || value === "installment",
        then: (schema) => schema.required("Installment Amount is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    installmentFrequency: Yup.string().when("paymentType", {
      is: (value) => value === "installments" || value === "installment",
      then: (schema) => schema.required("Installment Frequency is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    firstPaymentDate: Yup.date().when("paymentType", {
      is: (value) => value === "installments" || value === "installment",
      then: (schema) => schema.required("First Payment Date is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    interestRate: Yup.number()
      .typeError("Interest Rate must be numeric")
      .min(0, "Interest Rate cannot be negative")
      .when("paymentType", {
        is: (value) => value === "installments" || value === "installment",
        then: (schema) => schema.required("Interest Rate is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    financingInstitution: Yup.string().when("paymentType", {
      is: (value) => value === "installments" || value === "installment",
      then: (schema) => schema.required("Financing Institution is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
  Yup.object().shape({
    insuranceProvider: Yup.string().required("Insurance Provider is required"),
    policyNumber: Yup.string().required("Policy Number is required"),
    coverageType: Yup.string().required("Coverage Type is required"),
    coverageAmount: Yup.number()
      .typeError("Coverage Amount must be numeric")
      .positive("Coverage Amount must be greater than zero")
      .required("Coverage Amount is required"),
    premiumAmount: Yup.number()
      .typeError("Premium Amount must be numeric")
      .positive("Premium Amount must be greater than zero")
      .required("Premium Amount is required"),
    insuranceStartDate: Yup.date().required("Start Date is required"),
    insuranceExpiryDate: Yup.date()
      .min(Yup.ref("insuranceStartDate"), "Expiry Date must be after Start Date")
      .required("Expiry Date is required"),
  }),
  Yup.object().shape({
    attachments: Yup.array().nullable(),
  }),
];

const normalizeAssetsRegistrationTab = (value, fallback = "equipments") => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["vehicles", "equipments", "tyres"].includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "equipments") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const getAssetsRegistrationReturnTab = (location, fallback = "equipments") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const capacityUnitOptions = [
  { label: "Litres", value: "litres" },
  { label: "Tons", value: "tons" },
  { label: "Units", value: "units" },
];

const usageMeterTypeOptions = [
  { label: "Trips", value: "trips" },
  { label: "Hours", value: "hours" },
  { label: "Volume", value: "volume" },
];

const equipmentStatusOptions = [
  { label: "Active", value: "active" },
  { label: "Under Maintenance", value: "under_maintenance" },
  { label: "Inactive", value: "inactive" },
  { label: "Decommissioned", value: "decommissioned" },
];

const equipmentPaymentTypeOptions = [
  { label: "Full Payment", value: "full_payment" },
  { label: "Installments", value: "installments" },
];

const equipmentPaymentMethodOptions = [
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "Cash", value: "cash" },
  { label: "Lease", value: "lease" },
  { label: "Financing", value: "financing" },
];

const depreciationMethodOptions = [
  { label: "Straight Line", value: "straight_line" },
  { label: "Reducing Balance", value: "reducing_balance" },
];

const coverageTypeOptions = [
  { label: "Comprehensive", value: "comprehensive" },
  { label: "Third Party", value: "third_party" },
];

const insuranceProviderOptions = [
  { label: "Adamjee Insurance", value: "adamjee_insurance" },
  { label: "EFU General Insurance", value: "efu_general" },
  { label: "Jubilee General Insurance", value: "jubilee_general" },
  { label: "State Life Insurance", value: "state_life" },
  { label: "TPL Insurance", value: "tpl_insurance" },
  { label: "IGI Insurance", value: "igi_insurance" },
  { label: "Pak Qatar General Takaful", value: "pak_qatar_takaful" },
  { label: "Alfalah Insurance", value: "alfalah_insurance" },
  { label: "Askari General Insurance", value: "askari_general" },
  { label: "UBL Insurers", value: "ubl_insurers" },
];

const renderSelectLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const CreateEquipment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getAssetsRegistrationPath(
    getAssetsRegistrationReturnTab(location, "equipments")
  );
  const editingEquipment = location?.state?.equipment || null;
  const isEditing = Boolean(editingEquipment?.systemId);
  const {
    createEquipmentLoading,
    dropdownLoadingCount,
    dropdownError,
    companyBranchesDropdownData,
    equipmentTypesDropdownData,
    equipmentManufacturersDropdownData,
    equipmentModelsDropdownData,
    equipmentVendorsDropdownData,
  } = useSelector((state) => state.equipment);

  const [currentFormStep, setCurrentFormStep] = useState(0);

  useEffect(() => {
    dispatch(clearCreateEquipmentState());
    dispatch(fetchCompanyBranchesDropdown());
    dispatch(fetchEquipmentTypesDropdown());
    dispatch(fetchEquipmentManufacturersDropdown());
    dispatch(fetchEquipmentModelsDropdown());
    dispatch(fetchEquipmentVendorsDropdown());

    return () => {
      dispatch(clearCreateEquipmentState());
    };
  }, [dispatch]);

  const dropdownLoading = dropdownLoadingCount > 0;
  const resolvedDropdownError = dropdownError;

  const branchDropdownOptions = useMemo(() => {
    const source = Array.isArray(companyBranchesDropdownData)
      ? companyBranchesDropdownData
      : findCollectionByKeys(companyBranchesDropdownData, [
          "companybranches",
          "branches",
          "branchdropdown",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "branch_id"],
      labelKeys: ["label", "branch_name", "name", "title"],
      defaultLabelPrefix: "Branch",
    });
  }, [companyBranchesDropdownData]);

  const equipmentTypeDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentTypesDropdownData)
      ? equipmentTypesDropdownData
      : findCollectionByKeys(equipmentTypesDropdownData, [
          "equipmenttypes",
          "equipmenttype",
          "equipmenttypedropdown",
          "types",
        ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "equipment_type_id"],
      labelKeys: ["label", "equipment_type_name", "name", "title"],
      defaultLabelPrefix: "Equipment Type",
    });

    return normalized;
  }, [equipmentTypesDropdownData]);

  const manufacturerDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentManufacturersDropdownData)
      ? equipmentManufacturersDropdownData
      : findCollectionByKeys(equipmentManufacturersDropdownData, [
          "equipmentmanufacturers",
          "equipmentmanufacturer",
          "manufacturers",
          "manufacturer",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "manufacturer_id"],
      labelKeys: ["label", "manufacturer_name", "name", "title"],
      defaultLabelPrefix: "Manufacturer",
    });
  }, [equipmentManufacturersDropdownData]);

  const modelDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentModelsDropdownData)
      ? equipmentModelsDropdownData
      : findCollectionByKeys(equipmentModelsDropdownData, [
          "equipmentmodels",
          "equipmentmodel",
          "models",
          "model",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "model_id"],
      labelKeys: ["label", "model_name", "name", "title"],
      defaultLabelPrefix: "Model",
    });
  }, [equipmentModelsDropdownData]);

  const vendorDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentVendorsDropdownData)
      ? equipmentVendorsDropdownData
      : findCollectionByKeys(equipmentVendorsDropdownData, [
          "equipmentvendors",
          "equipmentvendor",
          "suppliervendors",
          "vendors",
          "vendor",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "vendor_id", "supplier_vendor_id"],
      labelKeys: ["title", "label", "vendor_name", "name"],
      defaultLabelPrefix: "Vendor",
    });
  }, [equipmentVendorsDropdownData]);

  const initialValues = useMemo(
    () => getInitialEquipmentValues(editingEquipment),
    [editingEquipment]
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = buildCreateEquipmentPayload(values);
      await dispatch(createEquipmentApi(payload)).unwrap();

      successNotify(
        isEditing ? "Equipment updated successfully" : "Equipment created successfully"
      );
      navigate(returnPath);
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={equipmentStepSchemas[currentFormStep]}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        validateForm,
        handleSubmit,
        setTouched,
      }) => {
        const isLastStep = currentFormStep === equipmentFormSteps.length - 1;

        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function"
              ? updater(values.attachments || [])
              : updater;
          setFieldValue("attachments", nextFiles);
        };

        const handleNext = async () => {
          const allErrors = await validateForm();
          const activeStepFields = equipmentStepFields[currentFormStep] || [];
          const hasCurrentStepErrors = activeStepFields.some((fieldName) =>
            Object.prototype.hasOwnProperty.call(allErrors, fieldName)
          );

          if (hasCurrentStepErrors) {
            const touchedMap = {};
            activeStepFields.forEach((fieldName) => {
              touchedMap[fieldName] = true;
            });
            setTouched(touchedMap);
            return;
          }

          setCurrentFormStep((previous) => previous + 1);
        };

        const handlePrevious = () => {
          setCurrentFormStep((previous) => Math.max(0, previous - 1));
        };

        const handleTopSave = async () => {
          if (!isLastStep) {
            await handleNext();
            return;
          }
          handleSubmit();
        };

        return (
          <div className="create_vehicle_page create_equipment_page">
            <Breadcrumbs
              values={["Assets", "Equipments", "Equipment Registration"]}
            />

            <div className="page_header access_view">
              <h2>Equipment Registration</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate(returnPath)}
                  submitBtn={handleTopSave}
                  submitText="Save"
                  type="button"
                  shouldRow={false}
                  loading={createEquipmentLoading}
                  disabled={createEquipmentLoading}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={9}>
                  <div className="trip_information_main_div">
                    <h5>Registration Information</h5>

                    {resolvedDropdownError && (
                      <div
                        className="error-message"
                        style={{ color: "#ff0000de", marginBottom: "12px" }}
                      >
                        {resolvedDropdownError}
                      </div>
                    )}

                    <div>
                      <h6>
                        System ID: <span>{values.systemId}</span>
                      </h6>

                      <div className="d-flex trip_create_steps_layout">
                        <div className="trip_create_steps_sidebar">
                          <Steps
                            className="create_vehicle_steps form_steps_vertical"
                            current={currentFormStep}
                            direction="vertical"
                            items={equipmentFormSteps}
                            onChange={async (targetStep) => {
                              if (targetStep <= currentFormStep) {
                                setCurrentFormStep(targetStep);
                                return;
                              }

                              const allErrors = await validateForm();
                              const activeStepFields = equipmentStepFields[currentFormStep] || [];
                              const hasCurrentStepErrors = activeStepFields.some((fieldName) =>
                                Object.prototype.hasOwnProperty.call(allErrors, fieldName)
                              );

                              if (hasCurrentStepErrors) {
                                const touchedMap = {};
                                activeStepFields.forEach((fieldName) => {
                                  touchedMap[fieldName] = true;
                                });
                                setTouched(touchedMap);
                                return;
                              }

                              setCurrentFormStep(targetStep);
                            }}
                          />
                        </div>

                        <div className="w-100 mb-3 create_equipment_step_content">
                          {currentFormStep === 0 && (
                            <div className="create_equipment_general_info">
                              <div className="create_equipment_step_section">
                                <h6 className="create_equipment_step_title">Core Details</h6>
                                <Row>
                              <Col md={6}>
                                <Input
                                  label="Registration No."
                                  placeholder="Enter Registration No."
                                  name="registrationNumber"
                                  value={values.registrationNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.registrationNumber}
                                  touched={touched.registrationNumber}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label={renderSelectLabel("Equipment Type")}
                                  placeholder="Select Equipment Type"
                                  options={equipmentTypeDropdownOptions}
                                  value={
                                    findOptionByValueOrLabel(
                                      equipmentTypeDropdownOptions,
                                      values.equipmentType
                                    )
                                  }
                                  name="equipmentType"
                                  onChange={(option) =>
                                    setFieldValue("equipmentType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.equipmentType}
                                  touched={touched.equipmentType}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Serial Number"
                                  placeholder="Enter Serial Number"
                                  name="serialNumber"
                                  value={values.serialNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.serialNumber}
                                  touched={touched.serialNumber}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Capacity"
                                  placeholder="Enter Capacity"
                                  type="number"
                                  name="capacityValue"
                                  value={values.capacityValue}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.capacityValue}
                                  touched={touched.capacityValue}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label={renderSelectLabel("Capacity Unit")}
                                  placeholder="Select Capacity Unit"
                                  options={capacityUnitOptions}
                                  value={
                                    capacityUnitOptions.find(
                                      (option) => option.value === values.capacityUnit
                                    ) || null
                                  }
                                  name="capacityUnit"
                                  onChange={(option) =>
                                    setFieldValue("capacityUnit", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.capacityUnit}
                                  touched={touched.capacityUnit}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label={renderSelectLabel("Usage Meter Type")}
                                  placeholder="Select Usage Meter Type"
                                  options={usageMeterTypeOptions}
                                  value={
                                    usageMeterTypeOptions.find(
                                      (option) => option.value === values.usageMeterType
                                    ) || null
                                  }
                                  name="usageMeterType"
                                  onChange={(option) =>
                                    setFieldValue("usageMeterType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.usageMeterType}
                                  touched={touched.usageMeterType}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label={renderSelectLabel("Branch")}
                                  placeholder="Select Branch"
                                  options={branchDropdownOptions}
                                  value={
                                    findOptionByValueOrLabel(
                                      branchDropdownOptions,
                                      values.branch
                                    )
                                  }
                                  name="branch"
                                  onChange={(option) =>
                                    setFieldValue("branch", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.branch}
                                  touched={touched.branch}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label={renderSelectLabel("Status")}
                                  placeholder="Select Status"
                                  options={equipmentStatusOptions}
                                  value={
                                    equipmentStatusOptions.find(
                                      (option) => option.value === values.status
                                    ) || null
                                  }
                                  name="status"
                                  onChange={(option) =>
                                    setFieldValue("status", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.status}
                                  touched={touched.status}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={12}>
                                <Input
                                  label="Description"
                                  placeholder="Write detail of part if any..."
                                  name="description"
                                  value={values.description}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.description}
                                  touched={touched.description}
                                  quill
                                  setFieldValue={setFieldValue}
                                  required={false}
                                  className="create_vehicle_description"
                                />
                              </Col>
                              <Col md={12}>
                                <div className="create_equipment_section_block">
                                  <h6 className="create_equipment_section_title">
                                    Manufacturer Details
                                  </h6>

                                  <Row>
                                    <Col md={6}>
                                      <Select
                                        label={renderSelectLabel("Manufacturer")}
                                        placeholder="Select Manufacturer"
                                        options={manufacturerDropdownOptions}
                                        value={
                                          findOptionByValueOrLabel(
                                            manufacturerDropdownOptions,
                                            values.manufacturer
                                          )
                                        }
                                        name="manufacturer"
                                        onChange={(option) =>
                                          setFieldValue(
                                            "manufacturer",
                                            option ? option.value : ""
                                          )
                                        }
                                        onBlur={handleBlur}
                                        error={errors.manufacturer}
                                        touched={touched.manufacturer}
                                        isSearch={false}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={renderSelectLabel("Model")}
                                        placeholder="Select Model"
                                        options={modelDropdownOptions}
                                        value={
                                          findOptionByValueOrLabel(
                                            modelDropdownOptions,
                                            values.model
                                          )
                                        }
                                        name="model"
                                        onChange={(option) =>
                                          setFieldValue("model", option ? option.value : "")
                                        }
                                        onBlur={handleBlur}
                                        error={errors.model}
                                        touched={touched.model}
                                        isSearch={false}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Year of Manufacture"
                                        placeholder="YYYY"
                                        type="number"
                                        name="yearOfManufacture"
                                        value={values.yearOfManufacture}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.yearOfManufacture}
                                        touched={touched.yearOfManufacture}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={renderSelectLabel("Supplier / Vendor")}
                                        placeholder="Select Option"
                                        options={vendorDropdownOptions}
                                        value={
                                          findOptionByValueOrLabel(
                                            vendorDropdownOptions,
                                            values.supplierVendor
                                          )
                                        }
                                        name="supplierVendor"
                                        onChange={(option) =>
                                          setFieldValue(
                                            "supplierVendor",
                                            option ? option.value : ""
                                          )
                                        }
                                        onBlur={handleBlur}
                                        error={errors.supplierVendor}
                                        touched={touched.supplierVendor}
                                        loading={dropdownLoading}
                                        isSearch={false}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Asset Number"
                                        placeholder="Enter Asset Number"
                                        name="assetNumber"
                                        value={values.assetNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.assetNumber}
                                        touched={touched.assetNumber}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                              <Col md={12}>
                                <div className="create_equipment_section_block">
                                  <h6 className="create_equipment_section_title">
                                    Technical Specification
                                  </h6>

                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Number of Axle"
                                        placeholder="00"
                                        type="number"
                                        name="numberOfAxles"
                                        value={values.numberOfAxles}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.numberOfAxles}
                                        touched={touched.numberOfAxles}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={renderSelectLabel("Brake System")}
                                        placeholder="Select Brake System"
                                        options={brakeSystemOptions}
                                        value={
                                          findOptionByValueOrLabel(
                                            brakeSystemOptions,
                                            values.brakeSystem
                                          )
                                        }
                                        name="brakeSystem"
                                        onChange={(option) =>
                                          setFieldValue("brakeSystem", option ? option.value : "")
                                        }
                                        onBlur={handleBlur}
                                        error={errors.brakeSystem}
                                        touched={touched.brakeSystem}
                                        isSearch={false}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Number of Tyres"
                                        placeholder="00"
                                        type="number"
                                        name="numberOfTyres"
                                        value={values.numberOfTyres}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.numberOfTyres}
                                        touched={touched.numberOfTyres}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={renderSelectLabel("Suspension Type")}
                                        placeholder="Select Type"
                                        options={suspensionTypeOptions}
                                        value={
                                          findOptionByValueOrLabel(
                                            suspensionTypeOptions,
                                            values.suspensionType
                                          )
                                        }
                                        name="suspensionType"
                                        onChange={(option) =>
                                          setFieldValue(
                                            "suspensionType",
                                            option ? option.value : ""
                                          )
                                        }
                                        onBlur={handleBlur}
                                        error={errors.suspensionType}
                                        touched={touched.suspensionType}
                                        isSearch={false}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                              <Col md={12}>
                                <div className="create_equipment_section_block">
                                  <h6 className="create_equipment_section_title">
                                    Capacity Information
                                  </h6>

                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Tanker / Bowser Capacity"
                                        placeholder="00"
                                        type="number"
                                        name="tankerBowserCapacity"
                                        value={values.tankerBowserCapacity}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.tankerBowserCapacity}
                                        touched={touched.tankerBowserCapacity}
                                        showExtraText
                                        extraText="Liters"
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Number of Compartments"
                                        placeholder="00"
                                        type="number"
                                        name="numberOfCompartments"
                                        value={values.numberOfCompartments}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.numberOfCompartments}
                                        touched={touched.numberOfCompartments}
                                        showExtraText
                                        extraText="Liters"
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Load Capacity"
                                        placeholder="00"
                                        type="number"
                                        name="loadCapacity"
                                        value={values.loadCapacity}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.loadCapacity}
                                        touched={touched.loadCapacity}
                                        showExtraText
                                        extraText="Kg / Tons"
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={renderSelectLabel("Container Size")}
                                        placeholder="Select Size"
                                        options={containerSizeOptions}
                                        value={
                                          findOptionByValueOrLabel(
                                            containerSizeOptions,
                                            values.containerSize
                                          )
                                        }
                                        name="containerSize"
                                        onChange={(option) =>
                                          setFieldValue("containerSize", option ? option.value : "")
                                        }
                                        onBlur={handleBlur}
                                        error={errors.containerSize}
                                        touched={touched.containerSize}
                                        isSearch={false}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                              <Col md={12}>
                                <div className="create_equipment_section_block">
                                  <h6 className="create_equipment_section_title">
                                    Dimensional Data
                                  </h6>

                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Length"
                                        placeholder="00"
                                        type="number"
                                        name="length"
                                        value={values.length}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.length}
                                        touched={touched.length}
                                        showExtraText
                                        extraText="mm / Meters"
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Height"
                                        placeholder="00"
                                        type="number"
                                        name="height"
                                        value={values.height}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.height}
                                        touched={touched.height}
                                        showExtraText
                                        extraText="mm / Meters"
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Width"
                                        placeholder="00"
                                        type="number"
                                        name="width"
                                        value={values.width}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.width}
                                        touched={touched.width}
                                        showExtraText
                                        extraText="mm / Meters"
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Tare Weight"
                                        placeholder="00"
                                        type="number"
                                        name="tareWeight"
                                        value={values.tareWeight}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.tareWeight}
                                        touched={touched.tareWeight}
                                        showExtraText
                                        extraText="Kg"
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Gross Weight"
                                        placeholder="00"
                                        type="number"
                                        name="grossWeight"
                                        value={values.grossWeight}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.grossWeight}
                                        touched={touched.grossWeight}
                                        showExtraText
                                        extraText="Kg (tons)"
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                            </Row>
                              </div>
                            </div>
                          )}

                          {currentFormStep === 1 && (
                            <Row>
                              <Col md={6}>
                                <Input
                                  label="Purchase Date"
                                  type="date"
                                  name="purchaseDate"
                                  value={values.purchaseDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.purchaseDate}
                                  touched={touched.purchaseDate}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Purchase Cost"
                                  type="number"
                                  placeholder="00"
                                  name="purchaseCost"
                                  value={values.purchaseCost}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.purchaseCost}
                                  touched={touched.purchaseCost}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Payment Type"
                                  placeholder="Select Payment Type"
                                  options={equipmentPaymentTypeOptions}
                                  value={
                                    equipmentPaymentTypeOptions.find(
                                      (option) => option.value === values.paymentType
                                    ) || null
                                  }
                                  name="paymentType"
                                  onChange={(option) =>
                                    setFieldValue("paymentType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.paymentType}
                                  touched={touched.paymentType}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Payment Method"
                                  placeholder="Select Payment Method"
                                  options={equipmentPaymentMethodOptions}
                                  value={
                                    equipmentPaymentMethodOptions.find(
                                      (option) => option.value === values.paymentMethod
                                    ) || null
                                  }
                                  name="paymentMethod"
                                  onChange={(option) =>
                                    setFieldValue("paymentMethod", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.paymentMethod}
                                  touched={touched.paymentMethod}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Vendor"
                                  placeholder="Select Vendor"
                                  options={vendorDropdownOptions}
                                  value={
                                    findOptionByValueOrLabel(vendorDropdownOptions, values.vendor)
                                  }
                                  name="vendor"
                                  onChange={(option) =>
                                    setFieldValue("vendor", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.vendor}
                                  touched={touched.vendor}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Depreciation Method"
                                  placeholder="Select Depreciation Method"
                                  options={depreciationMethodOptions}
                                  value={
                                    depreciationMethodOptions.find(
                                      (option) => option.value === values.depreciationMethod
                                    ) || null
                                  }
                                  name="depreciationMethod"
                                  onChange={(option) =>
                                    setFieldValue("depreciationMethod", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.depreciationMethod}
                                  touched={touched.depreciationMethod}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Useful Life"
                                  type="number"
                                  placeholder="00"
                                  name="usefulLife"
                                  value={values.usefulLife}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.usefulLife}
                                  touched={touched.usefulLife}
                                  showExtraText
                                  extraText="Years"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Residual Value"
                                  type="number"
                                  placeholder="00"
                                  name="residualValue"
                                  value={values.residualValue}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.residualValue}
                                  touched={touched.residualValue}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>

                              {isInstallmentPayment(values.paymentType) && (
                                <>
                                  <Col md={12}>
                                    <div className="create_vehicle_divider" />
                                    <h6 className="create_vehicle_subheading">
                                      Installments Details
                                    </h6>
                                  </Col>

                                  <Col md={6}>
                                    <Input
                                      label="Total Installments"
                                      type="number"
                                      placeholder="00"
                                      name="totalInstallments"
                                      value={values.totalInstallments}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.totalInstallments}
                                      touched={touched.totalInstallments}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Installment Amount"
                                      type="number"
                                      placeholder="00"
                                      name="installmentAmount"
                                      value={values.installmentAmount}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.installmentAmount}
                                      touched={touched.installmentAmount}
                                      showExtraText
                                      extraText="PKR"
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Select
                                      label="Installment Frequency"
                                      placeholder="Select Installment Frequency"
                                      options={installmentFrequencyOptions}
                                      value={
                                        installmentFrequencyOptions.find(
                                          (option) =>
                                            option.value === values.installmentFrequency
                                        ) || null
                                      }
                                      name="installmentFrequency"
                                      onChange={(option) =>
                                        setFieldValue(
                                          "installmentFrequency",
                                          option ? option.value : ""
                                        )
                                      }
                                      onBlur={handleBlur}
                                      error={errors.installmentFrequency}
                                      touched={touched.installmentFrequency}
                                      isSearch={false}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="First Payment Date"
                                      type="date"
                                      name="firstPaymentDate"
                                      value={values.firstPaymentDate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.firstPaymentDate}
                                      touched={touched.firstPaymentDate}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Interest Rate"
                                      type="number"
                                      placeholder="00"
                                      name="interestRate"
                                      value={values.interestRate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.interestRate}
                                      touched={touched.interestRate}
                                      showExtraText
                                      extraText="%"
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Financing Institution"
                                      placeholder="Enter Financing Institution"
                                      name="financingInstitution"
                                      value={values.financingInstitution}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.financingInstitution}
                                      touched={touched.financingInstitution}
                                    />
                                  </Col>
                                </>
                              )}
                            </Row>
                          )}

                          {currentFormStep === 2 && (
                            <Row>
                              <Col md={6}>
                                <Select
                                  label="Insurance Provider"
                                  placeholder="Select Insurance Provider"
                                  options={insuranceProviderOptions}
                                  value={
                                    insuranceProviderOptions.find(
                                      (option) => option.value === values.insuranceProvider
                                    ) || null
                                  }
                                  name="insuranceProvider"
                                  onChange={(option) =>
                                    setFieldValue(
                                      "insuranceProvider",
                                      option ? option.value : ""
                                    )
                                  }
                                  onBlur={handleBlur}
                                  error={errors.insuranceProvider}
                                  touched={touched.insuranceProvider}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Policy Number"
                                  placeholder="Enter Policy Number"
                                  name="policyNumber"
                                  value={values.policyNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.policyNumber}
                                  touched={touched.policyNumber}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Coverage Type"
                                  placeholder="Select Coverage Type"
                                  options={coverageTypeOptions}
                                  value={
                                    coverageTypeOptions.find(
                                      (option) => option.value === values.coverageType
                                    ) || null
                                  }
                                  name="coverageType"
                                  onChange={(option) =>
                                    setFieldValue("coverageType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.coverageType}
                                  touched={touched.coverageType}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Coverage Amount"
                                  placeholder="00"
                                  type="number"
                                  name="coverageAmount"
                                  value={values.coverageAmount}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.coverageAmount}
                                  touched={touched.coverageAmount}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Premium Amount"
                                  placeholder="00"
                                  type="number"
                                  name="premiumAmount"
                                  value={values.premiumAmount}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.premiumAmount}
                                  touched={touched.premiumAmount}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Start Date"
                                  type="date"
                                  name="insuranceStartDate"
                                  value={values.insuranceStartDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.insuranceStartDate}
                                  touched={touched.insuranceStartDate}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Expiry Date"
                                  type="date"
                                  name="insuranceExpiryDate"
                                  value={values.insuranceExpiryDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.insuranceExpiryDate}
                                  touched={touched.insuranceExpiryDate}
                                />
                              </Col>
                            </Row>
                          )}

                          {currentFormStep === 3 && (
                            <Row>
                              <Col md={12}>
                                <div className="issue_file_upload_wrapper">
                                  <label>Attachment</label>
                                  <FileUpload
                                    destination="equipment-registration"
                                    files={values.attachments}
                                    setFiles={updateAttachments}
                                    maxFileSize={10}
                                    maxFiles={5}
                                    className="issue_file_upload create_vehicle_upload"
                                  />
                                  {touched.attachments && errors.attachments && (
                                    <div
                                      className="error-message"
                                      style={{
                                        color: "#ff0000de",
                                        fontSize: "12px",
                                        marginTop: "3px",
                                        marginLeft: "5px",
                                      }}
                                    >
                                      {errors.attachments}
                                    </div>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          )}

                          <Row>
                            <Col md={12}>
                              <div className="d-flex align-items-center gap-2 create_equipment_actions">
                                <Button
                                  cancelBtn={handlePrevious}
                                  isShowCancel={currentFormStep !== 0}
                                  submitText={isLastStep ? "Save" : "Next"}
                                  type="button"
                                  shouldRow={false}
                                  submitBtn={isLastStep ? handleSubmit : handleNext}
                                  cancelBtnText="Previous"
                                  loading={createEquipmentLoading}
                                  disabled={createEquipmentLoading}
                                  cancelBtnDisabled={createEquipmentLoading}
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <div className="trip_notes_main_div create_vehicle_notes">
                    <h5>Notes</h5>
                    <ReactQuill
                      placeholder="Write notes here..."
                      modules={modules}
                      formats={formats}
                      value={values.notes || ""}
                      onChange={(content) => setFieldValue("notes", content)}
                      className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default CreateEquipment;
